import { Controller } from "@hotwired/stimulus"

// Controla a revelação mágica do texto (efeito “caneta”) e exibe o footer ao final.
export default class extends Controller {
  static values = {
    skipIntro: { type: Boolean, default: false },
    nextUrl: String,
    llmResponseUrl: String,
    brushStampUrl: String,
    brushStampFallbackUrl: String,
    guestToken: String,
  }

  connect() {
    this.body = this.element.querySelector(".interior-body")
    this.textContainer = this.body?.querySelector(".interior-text") ?? this.body
    this.textClickHandler = (event) => this.handleNarrationTap(event)
    this.element.addEventListener("click", this.textClickHandler)
    this.paragraphs = this.findTextBlocks(this.textContainer)
    this.footer = this.element.querySelector(".interior-footer")
    this.llmFooterPlate = this.element.querySelector(".llm-footer-plate")
    this.glow = document.getElementById("narration-glow")
    this.hero = this.element.querySelector(".interior-hero")
    this.scribeBright = document.getElementById("scribe-bright")
    this.starsLayer = document.getElementById("scribe-stars-layer")
    this.glowStarsLayer = document.getElementById("glow-stars-layer")
    this.continueButton = this.element.querySelector('[data-role="continue"]')
    this.llmPromptInput = this.element.querySelector(".llm-prompt-input")
    this.llmEnvelopeBtn = this.element.querySelector(".llm-envelope-btn")
    this.llmCardsLocked = false
    this.storagePrefix = "narrationSeen:"
    this.forcePrefix = "narrationForce:"
    this.sessionStore = this.safeStorage("sessionStorage")
    this.persistentStore = this.safeStorage("localStorage")
    this.storageKey = this.computeStorageKey()
    this.forceKey = this.computeForceKey()
    this.introStorageKey = `invitationIntroSeen:${this.guestTokenValue || "unknown"}`
    this.badgeStorageKey = this.computeBadgeStorageKey()

    this.setupLlmSuggestions()
    this.setupBadgesArea()
    this.middleArea = this.element.querySelector("#llm-middle-area")
    this.mode = this.middleArea?.dataset?.mode || "suggestions"
    this.flowActive = this.mode === "flow"
    this.panelActive = this.mode === "panel"
    this.syncFlowInput()

    // Setup scroll indicator
    this.setupScrollIndicator()

    // Phase 3 (docs/CONCIERGE_REDESIGN.md): the transcript is ephemeral. The
    // server is the single source of truth for conversation state; replaying
    // a stored transcript over a fresh server render caused illegal
    // combinations. Purge the legacy storage key.
    this.persistentStore.removeItem(`wm_chat_${this.guestTokenValue || "anon"}`)
    this.turnVersion = null
    try {
      this.pendingOperation = JSON.parse(this.sessionStore?.getItem("wm:pending-turn") || "null")
    } catch (_) { this.pendingOperation = null }
    this.sendAssistantRequest(this.pendingOperation
      ? { event: "recover_turn", operation_id: this.pendingOperation.operation_id }
      : { event: "turn_state" })

    // Show footer immediately - it should always be visible
    if (this.footer) this.footer.classList.add("interior-footer--visible")

    // If we're missing critical elements, show suggestions and bail out
    if (!this.glow || !this.body || !this.scribeBright) {
      if (this.mode === "suggestions") this.revealLlmSuggestions({ animate: false })
      return
    }

    // If no paragraphs but we have all elements, just show content
    if (!this.paragraphs.length) {
      if (this.mode === "suggestions") this.revealLlmSuggestions({ animate: false })
      return
    }

    this.glow.classList.remove("narration-glow--llm-loading")

    // UX-real calibration (NN/g attention budget): 18ms/char keeps a full
    // response under ~4s; tap-to-complete covers the rest.
    this.typeSpeed = 18
    this.pauseBetweenParagraphs = 200
    this.brightSize = 54
    this.glowStarsInterval = null

    this.prepareParagraphs()
    this.bindContinue()
    this.magicAnimating = false
    this.magicSkipped = false
    this.animationRunId = 0
    this.skipMagicBtn = this.element.querySelector('[data-role="skip-magic"]')

    this.cleanFromLlmParam()
    const navType = this.navigationType()
    this.isReloadNavigation = navType === "reload" || navType === "back_forward"
    const forcedRun = this.sessionStore?.getItem(this.forceKey) === "1"
    if (forcedRun) {
      this.sessionStore.removeItem(this.forceKey)
    }
    this.seenBefore =
      !this.isReloadNavigation &&
      !forcedRun &&
      (this.sessionStore?.getItem(this.storageKey) === "1" || this.persistentIntroSeen())
    if (this.skipIntroValue || this.seenBefore) {
      this.revealAll()
      return
    }

    this.magicAnimating = true
    this.magicSkipped = false
    this.animationRunId += 1
    const currentRunId = this.animationRunId
    this.showSkipButton()
    this.showGlow()
    // Reveal badges first, then paragraphs
    this.revealBadgesArea({ animate: true })
    setTimeout(() => {
      if (this.animationRunId !== currentRunId) return
      this.revealParagraph(0, currentRunId)
    }, 300)
  }

  disconnect() {
    if (this.glowStarsInterval) clearInterval(this.glowStarsInterval)
    this.element.removeEventListener("click", this.textClickHandler)

    // Clean up scroll listener
    if (this.scrollOverlay && this.scrollHandler) {
      this.scrollOverlay.removeEventListener("scroll", this.scrollHandler)
    }

    // Clean up resize listener
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
    }

    // CRITICAL: Force unlock screen to prevent the loader from staying visible
    // when navigating away during an animation or request
    if (window.screenLock && this.currentLockId) {
      window.screenLock.unlock(this.currentLockId)
      this.currentLockId = null
    }
    // Also force unlock if there's any lingering lock from this controller
    if (window.screenLock && window.screenLock.isLocked()) {
      window.screenLock.forceUnlock()
    }
  }

  safeStorage(name) {
    const memory = new Map()
    let native
    try { native = window[name] } catch (_) { native = null }
    const unavailable = () => {
      native = null
      storage.durable = false
      if (name === "sessionStorage") {
        this.recoveryStorageUnavailable = true
        this.warnRecoveryUnavailable()
      }
    }
    const storage = {
      durable: Boolean(native),
      getItem: key => {
        if (native) {
          try {
            const value = native.getItem(key)
            if (value === null) memory.delete(key)
            else memory.set(key, value)
            return value
          } catch (_) { unavailable() }
        }
        return memory.has(key) ? memory.get(key) : null
      },
      setItem: (key, value) => {
        memory.set(key, String(value))
        if (native) {
          try { native.setItem(key, String(value)) } catch (_) { unavailable() }
        }
      },
      removeItem: key => {
        memory.delete(key)
        if (native) {
          try { native.removeItem(key) } catch (_) { unavailable() }
        }
      }
    }
    if (!native) unavailable()
    return storage
  }

  warnRecoveryUnavailable() {
    if (!this.textContainer || this.textContainer.querySelector('[data-role="recovery-storage-warning"]')) return
    const warning = document.createElement("p")
    warning.dataset.role = "recovery-storage-warning"
    warning.setAttribute("role", "status")
    warning.textContent = "O armazenamento deste browser está indisponível. Podes tentar novamente nesta página, mas a recuperação automática após recarregar não está disponível. Mantém a página aberta; se a recarregares, consulta o estado guardado antes de repetir um pedido."
    this.textContainer.appendChild(warning)
  }

  // Setup scroll indicator to show when at bottom
  setupScrollIndicator() {
    this.scrollOverlay = this.element.querySelector(".interior-overlay")
    if (!this.scrollOverlay) return

    // Find scroll indicator button (now outside overlay)
    this.scrollIndicatorBtn = this.element.querySelector(".scroll-indicator")

    this.scrollHandler = () => {
      const { scrollTop, scrollHeight, clientHeight } = this.scrollOverlay
      const atBottom = scrollTop + clientHeight >= scrollHeight - 30
      // Only show when there's significant scroll (more than 80px)
      const hasScroll = scrollHeight > clientHeight + 80
      this.scrollOverlay.dataset.atBottom = atBottom ? "true" : "false"
      this.scrollOverlay.dataset.scrollable = hasScroll ? "true" : "false"

      // Toggle visible class on scroll indicator
      if (this.scrollIndicatorBtn) {
        const shouldShow = hasScroll && !atBottom
        this.scrollIndicatorBtn.classList.toggle("scroll-indicator--visible", shouldShow)
        this.scrollIndicatorBtn.setAttribute("aria-hidden", shouldShow ? "false" : "true")
      }
    }

    this.scrollOverlay.addEventListener("scroll", this.scrollHandler, { passive: true })

    // Initial check
    requestAnimationFrame(() => this.scrollHandler())

    // Re-check on resize (content might become scrollable)
    this.resizeHandler = () => requestAnimationFrame(() => this.scrollHandler())
    window.addEventListener("resize", this.resizeHandler, { passive: true })
  }

  // Handle scroll indicator click - scroll down partially
  handleScrollIndicatorClick(event) {
    event.preventDefault()
    if (!this.scrollOverlay) return

    // Scroll by 60% of the visible height for a comfortable partial scroll
    const scrollAmount = this.scrollOverlay.clientHeight * 0.6
    this.scrollOverlay.scrollBy({
      top: scrollAmount,
      behavior: "smooth"
    })
  }

  // Scroll to bottom of content - fixes iPad vh issues
  scrollToBottom() {
    if (!this.scrollOverlay) return

    // Small delay to ensure DOM has updated
    requestAnimationFrame(() => {
      this.scrollOverlay.scrollTo({
        top: this.scrollOverlay.scrollHeight,
        behavior: "smooth"
      })
    })
  }

  // Find text blocks for animation: prefer <p> tags, otherwise direct children, or the container itself
  findTextBlocks(container) {
    if (!container) return []

    // First try <p> tags
    let blocks = Array.from(container.querySelectorAll("p"))
    if (blocks.length) return blocks

    // Then try direct block-level children (div, h1-h6, etc.)
    blocks = Array.from(container.children).filter(el =>
      el.textContent?.trim() && !el.classList.contains("interior-text")
    )
    if (blocks.length) return blocks

    // Fallback: use the container itself as one block
    if (container.textContent?.trim()) return [container]

    return []
  }

  handleSkipMagic(event) {
    event?.preventDefault?.()
    if (!this.magicAnimating) return
    if (this.magicSkipped) return

    this.magicSkipped = true
    this.animationRunId += 1  // Invalidate all pending animation callbacks
    this.recordIntroSkip()
    this.skipAllMagic()
  }

  skipAllMagic() {
    // Revelar todos os parágrafos instantaneamente
    this.paragraphSpans?.forEach((spans) => {
      spans.forEach((span) => span.classList.add("reveal-char--visible"))
    })

    // Revelar badges instantaneamente
    if (this.llmBadgesArea) {
      this.llmBadgesArea.dataset.brushState = "revealed"
      this.llmBadgesArea.setAttribute("aria-hidden", "false")
    }
    if (this.llmBadgesWrap) {
      this.llmBadgesWrap.dataset.brushState = "revealed"
    }

    // Revelar cards instantaneamente
    if (this.middleArea) {
      this.middleArea.setAttribute("aria-hidden", "false")
    }
    if (this.llmSuggestionsGrid) {
      this.llmSuggestionsGrid.dataset.brushState = "revealed"
      this.llmSuggestionsGrid.setAttribute("aria-hidden", "false")
    }
    this.llmSuggestionCards?.forEach((card) => {
      card.classList.remove("llm-suggestion-card--brushing")
      card.classList.add("llm-suggestion-card--brushed")
      card.disabled = this.llmCardsLocked
      card.querySelectorAll(".llm-suggestion-brush-canvas").forEach((c) => c.remove())
    })

    // Revelar footer instantaneamente
    if (this.footer) {
      this.footer.classList.remove("interior-footer--revealing")
      this.footer.classList.add("interior-footer--visible")
    }
    this.llmFooterPlate?.querySelectorAll(".llm-footer-brush-canvas").forEach((c) => c.remove())

    this.middleArea?.classList.remove("llm-middle-area--muted")
    this.hideBright()
    this.hideGlow()
    this.markSeen()
    this.unlockUi()
    this.magicAnimating = false
    this.hideSkipButton()
    // Allow future LLM responses to animate normally even if the intro was skipped.
    this.magicSkipped = false
    this.scrollToBottom()
  }

  showSkipButton() {
    if (!this.skipMagicBtn) return
    this.skipMagicBtn.setAttribute("data-visible", "true")
  }

  hideSkipButton() {
    if (!this.skipMagicBtn) return
    this.skipMagicBtn.setAttribute("data-visible", "false")
  }

  bindContinue() {
    if (this.continueButton && this.hasNextUrlValue) {
      this.continueButton.addEventListener("click", () => {
        this.forceNextRunForPath(this.nextUrlValue)
        window.location.href = this.nextUrlValue
      })
    }
  }

  handleLlmSend(event) {
    event?.preventDefault?.()
    if (this.llmCardsLocked || this.llmRequestInFlight || !this.hasLlmResponseUrlValue) {
      this.focusPromptInput()
      return
    }
    const prompt = this.llmPromptInput?.value?.trim() ?? ""
    if (!prompt) {
      this.focusPromptInput()
      return
    }
    // Guard against double submit — set flag immediately before any async work
    this.llmRequestInFlight = true
    // Show user message bubble (mask password fields)
    const isPassword = this.llmPromptInput?.type === "password"
    // Display-safe copy for the error/retry card; never expose password values (F43)
    this.lastUserMessageDisplay = isPassword ? "••••••••" : prompt
    // Restore focus after the response so the user can keep typing (F38)
    this.refocusAfterResponse = true
    this.appendUserBubble(isPassword ? "••••••••" : prompt)
    this.showPendingLine()
    this.showGlow()
    this.lockUi("O concierge está a escrever...")
    this.clearAndFocusPromptInput()
    const payload = { message: prompt }
    if (this.notificationsThreadActive()) {
      payload.event = "notifications_thread_message"
    }
    this.sendAssistantRequest(payload)
  }

  handleNarrationTap(event) {
    // Tap anywhere (except interactive elements) completes the current
    // reveal animation — intro and responses alike (NN/g: never trap the
    // user inside an animation).
    if (this.magicAnimating) {
      const interactive = event.target.closest?.("button, a, input, textarea, select, [data-ui-action], .llm-suggestion-card")
      if (!interactive) {
        this.magicSkipped = true
        this.animationRunId += 1
        this.skipAllMagic()
        return
      }
    }
    this.handleUserBubbleEdit(event)
  }

  handleUserBubbleEdit(event) {
    if (this.llmRequestInFlight) return
    const bubble = event.target.closest?.(".llm-user-bubble")
    if (!bubble || !this.textContainer?.contains(bubble)) return
    const text = bubble.textContent?.trim() ?? ""
    if (!text || /^•+$/.test(text)) return
    if (!this.llmPromptInput) return

    this.llmPromptInput.value = text
    this.llmPromptInput.focus()
  }

  handleNotificationsClick(event) {
    event?.preventDefault?.()
    if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return

    this.showGlow()
    this.lockUi("A carregar notificações...")
    this.sendAssistantRequest({ event: "open_panel", panel_key: "notifications" })
  }

  handleClearChat(event) {
    event?.preventDefault?.()
    if (!window.confirm("Apagar esta correspondência? A conversa recomeça limpa.")) return

    this.textContainer?.querySelectorAll(".llm-user-bubble, .llm-assistant-message").forEach((node) => node.remove())
    this.paragraphs = this.findTextBlocks(this.textContainer)
    this.scrollToBottom()
  }

  handleCardClick(event) {
    event?.preventDefault?.()
    const button = event?.currentTarget
    if (!button) return
    if (this.llmCardsLocked) return
    if (button.disabled || button.dataset.cardDisabled === "true") return

    const kind = button.dataset.cardKind || "view"
    const featureId = button.dataset.cardId

    // Add loading state to clicked card for immediate feedback
    button.classList.add("llm-suggestion-card--loading")
    this.activeCard = button

    if (kind === "view") {
      if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return
      if (!featureId) return

      this.showGlow()
      this.lockUi("A carregar conteúdo...")
      this.sendAssistantRequest({ event: "open_panel", feature_id: featureId })
      return
    }

    if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return
    if (!featureId) return

    this.showGlow()
    this.lockUi("A preparar...")
    this.sendAssistantRequest({ event: "start_flow", feature_id: featureId })
  }

  handleFlowUiAction(event) {
    event?.preventDefault?.()
    const button = event?.currentTarget
    if (!button) return
    if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return

    const uiAction = button.dataset.uiAction?.trim() ?? ""
    const rawPayload = button.dataset.uiPayload ?? "{}"
    let uiPayload = {}
    try {
      uiPayload = JSON.parse(rawPayload)
    } catch (_) {
      uiPayload = {}
    }

    // Add loading state to clicked button for immediate feedback
    button.classList.add("btn--loading", "btn--selected")
    this.activeFlowButton = button

    // Echo the choice in the transcript so the guest's answer is visible,
    // exactly like typed messages (never echo password-ish labels — none exist,
    // but keep the mask guard consistent with handleLlmSend).
    const label = button.textContent?.trim() ?? ""
    if (label) this.appendUserBubble(label)

    this.showGlow()
    this.lockUi("A processar...")
    this.sendAssistantRequest({ ui_action: uiAction, ui_payload: uiPayload })
  }

  handleGalleryUploadSuccess(event) {
    const fileCount = Number(event?.detail?.file_count || 0)
    const uploadId = Number(event?.detail?.upload_id || 0)
    if (!Number.isSafeInteger(uploadId) || uploadId <= 0 || this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return

    this.appendUserBubble(`${fileCount} ficheiro${fileCount === 1 ? "" : "s"} enviado${fileCount === 1 ? "" : "s"}`)
    this.showGlow()
    this.lockUi("A guardar as tuas memórias...")
    this.sendAssistantRequest({ ui_action: "submit_gallery", ui_payload: { upload_id: uploadId } })
  }

  handlePanelBack(event) {
    event?.preventDefault?.()
    if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return

    this.showGlow()
    this.lockUi("A voltar...")
    this.sendAssistantRequest({ event: "close_panel" })
  }

  handleShowSuggestions(event) {
    event?.preventDefault?.()
    if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return

    this.showGlow()
    this.lockUi("A carregar sugestões...")
    this.sendAssistantRequest({ event: "show_suggestions" })
  }

  requestFlowSuggestions(event) {
    event?.preventDefault?.()
    if (this.llmRequestInFlight || !this.hasLlmResponseUrlValue) return

    this.showGlow()
    this.lockUi("A carregar opções...")
    this.sendAssistantRequest({ event: "show_suggestions" })
  }

  async sendAssistantRequest(payload = {}) {
    if (!this.hasLlmResponseUrlValue) return
    const controlRequest = ["turn_state", "recover_turn"].includes(payload.event)
    if (!controlRequest && this.pendingOperation && payload.operation_id !== this.pendingOperation.operation_id) {
      this.unlockUi()
      this.renderAssistantError("O pedido anterior ainda precisa de confirmação. Tenta recuperá-lo antes de enviar outro.")
      return
    }
    if (!controlRequest && !payload.operation_id) {
      if (!this.turnVersion) {
        this.unlockUi()
        this.renderAssistantError("Atualiza a página para recuperar o passo atual.")
        return
      }
      payload = { ...payload, operation_id: crypto.randomUUID(), expected_version: this.turnVersion }
    }
    if (!controlRequest) {
      this.pendingOperation = { operation_id: payload.operation_id, expected_version: payload.expected_version }
      try { this.sessionStore?.setItem("wm:pending-turn", JSON.stringify(this.pendingOperation)) } catch (_) {}
    }
    this.llmRequestInFlight = true
    this.lastAssistantPayload = payload
    this.removeAssistantError()
    // The resume choice is a one-shot card rendered by the server on page
    // load; any interaction (button, card or typed message) supersedes it —
    // it must never linger next to newer flow actions.
    if (!controlRequest) this.element.querySelector('[data-role="resume-choice"]')?.remove()
    this.toggleLlmLoading(true)

    // Keep the visible fallback within the 20-second UX budget.
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 19000)

    try {
      const response = await fetch(this.llmResponseUrlValue, {
        method: "POST",
        headers: this.llmRequestHeaders(),
        body: JSON.stringify(payload),
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      if (!response.ok) {
        let serverMessage = ""
        let body
        try {
          body = await response.json()
          serverMessage = typeof body?.error === "string" ? body.error : ""
        } catch (_) {
          // non-JSON error body; keep generic message
        }
        if (response.status === 401 && body?.status === "login_required") {
          // Authentication recovery is navigation, never a replay of the submitted input.
          this.lastAssistantPayload = null
          this.lastUserMessageDisplay = null
          this.refocusAfterResponse = false
          this.clearPendingOperation()
          let destination
          try {
            if (typeof body.redirect_url === "string" && body.redirect_url.trim()) {
              destination = new URL(body.redirect_url, window.location.href)
            }
          } catch (_) {
            // Invalid redirects fall through to the visible error without a retry payload.
          }
          if (destination && ["http:", "https:"].includes(destination.protocol) &&
              destination.origin === window.location.origin && !destination.username && !destination.password) {
            this.hideGlow()
            this.unlockUi()
            window.location.assign(destination.href)
            return
          }
        }
        if (body?.operation_status === "conflict" || body?.retryable === false) {
          this.lastAssistantPayload = null
          this.clearPendingOperation()
        }
        const failure = new Error(serverMessage || `LLM response ${response.status}`)
        failure.userMessage = serverMessage
        throw failure
      }
      const reply = await response.json()
      if (reply.turn_version) this.turnVersion = reply.turn_version
      if (reply.operation_status === "processed") {
        this.clearPendingOperation()
        this.lastAssistantPayload = null
      }
      if (payload.event === "turn_state") {
        this.unlockUi()
        return
      }
      this.removePendingLine()

      try {
        const htmlLen = typeof reply.html === "string" ? reply.html.length : 0
        const middleLen = typeof reply.middle_area_html === "string" ? reply.middle_area_html.length : 0
        console.debug("assistant:reply", { mode: reply.mode, htmlLen, middleLen, celebrate: Boolean(reply.celebrate) })
      } catch (_) {
        // ignore
      }

      if (reply.middle_area_html) {
        this.injectMiddleArea(reply.middle_area_html, reply.mode)
      } else {
        if (reply.cards_html) this.injectSuggestions(reply.cards_html)
        if ("flow_ui_html" in reply) this.injectFlowUi(reply.flow_ui_html || "")
        if ("panel_html" in reply) this.injectPanel(reply.panel_html || "")
      }
      if (reply.celebrate) {
        this.celebrateOnce()
      }

      if (reply.html) {
        this.toggleLlmLoading(false)
        // Unlock UI immediately when response arrives - animation will play without blocking
        this.unlockUi()
        try {
          this.injectLlmContent(reply.html)
          if (!this.paragraphs?.length) {
            console.warn("assistant:render: no paragraphs found after injection; using container children")
            this.paragraphs = Array.from(this.textContainer.lastElementChild?.children || [])
          }
          this.revealNewContent()
        } catch (error) {
          console.error("assistant:render failed; appending raw HTML", error)
          const fallback = document.createElement("div")
          fallback.className = "llm-assistant-message"
          fallback.innerHTML = reply.html
          this.textContainer.appendChild(fallback)
          this.revealMiddleArea({ animate: true })
          this.revealFooter({ animate: true })
          this.hideGlow()
          this.syncFlowInput()
          this.scrollToBottom()
          return
        }
        this.syncFlowInput()
        return
      }
      // Show brief success feedback on active card before unlocking
      this.showCardSuccess()
      this.revealMiddleArea({ animate: true })
      this.revealFooter({ animate: true })
      this.hideGlow()
      this.unlockUi()
      this.syncFlowInput()
      this.scrollToBottom()
    } catch (error) {
      console.error("Failed to refresh LLM body", error)
      this.removePendingLine()
      this.hideGlow()
      this.unlockUi()
      this.syncFlowInput()
      const message =
        error?.name === "AbortError"
          ? "Estou com dificuldades em responder. Tenta já de seguida."
          : (error?.userMessage || "Não foi possível falar com o concierge.")
      this.renderAssistantError(message)
    } finally {
      clearTimeout(timeoutId)
      this.removePendingLine()
      this.toggleLlmLoading(false)
      this.llmRequestInFlight = false
      // F38: restore focus after typed-message responses so the user can keep
      // writing. Fine-pointer only — refocusing on touch devices would pop the
      // keyboard over the response the user is trying to read.
      if (this.refocusAfterResponse) {
        this.refocusAfterResponse = false
        if (window.matchMedia?.("(pointer: fine)")?.matches) {
          this.focusPromptInput()
        }
      }
    }
  }

  renderAssistantError(message) {
    this.removeAssistantError()
    if (!this.textContainer) return

    const card = document.createElement("div")
    card.className = "llm-assistant-error"
    card.setAttribute("role", "alert")
    card.dataset.role = "assistant-error"

    const text = document.createElement("p")
    text.className = "llm-assistant-error__text"
    text.textContent = message

    // F43: show which message is being retried (display-safe copy only; card
    // clicks and panel events carry no message and render no quote).
    const original =
      typeof this.lastAssistantPayload?.message === "string" && this.lastUserMessageDisplay
        ? this.lastUserMessageDisplay
        : ""

    const retry = document.createElement("button")
    retry.type = "button"
    retry.className = "llm-assistant-error__retry"
    retry.textContent = "Tentar novamente"
    retry.addEventListener("click", () => {
      card.remove()
      if (this.lastAssistantPayload) {
        this.showGlow()
        this.lockUi("A tentar novamente...")
        this.sendAssistantRequest(this.lastAssistantPayload)
      }
    })

    card.appendChild(text)
    if (original) {
      const quote = document.createElement("p")
      quote.className = "llm-assistant-error__original"
      quote.textContent = `A tua mensagem: «${original}»`
      card.appendChild(quote)
    }
    if (this.lastAssistantPayload) card.appendChild(retry)
    this.textContainer.appendChild(card)
    this.scrollToBottom()
  }

  clearPendingOperation() {
    this.pendingOperation = null
    try { this.sessionStore?.removeItem("wm:pending-turn") } catch (_) {}
  }

  removeAssistantError() {
    this.element
      .querySelectorAll('[data-role="assistant-error"]')
      .forEach((node) => node.remove())
  }

  showCardSuccess() {
    if (!this.activeCard) return

    const card = this.activeCard
    card.classList.remove("llm-suggestion-card--loading")
    card.classList.add("llm-suggestion-card--success")

    // Remove success class after animation completes
    setTimeout(() => {
      card.classList.remove("llm-suggestion-card--success")
    }, 600)
  }

  injectMiddleArea(html, mode) {
    const existing = this.element.querySelector("#llm-middle-area")

    if (existing) {
      existing.outerHTML = html

      this.middleArea = this.element.querySelector("#llm-middle-area")
      this.mode = mode || this.middleArea?.dataset?.mode || "suggestions"
      this.flowActive = this.mode === "flow"
      this.panelActive = this.mode === "panel"

      this.setupLlmSuggestions()

      // Keep hidden - will be revealed after text animation via revealMiddleArea
      if (this.middleArea) {
        this.middleArea.setAttribute("aria-hidden", "true")
      }
      if (this.llmSuggestionsGrid) {
        this.llmSuggestionsGrid.dataset.brushState = "pending"
      }
    } else {
      // Middle area doesn't exist, insert it in the right place
      const suggestionsArea = this.element.querySelector("#llm-suggestions-area")
      if (suggestionsArea) {
        // Insert before suggestions area
        suggestionsArea.insertAdjacentHTML("beforebegin", html)
      } else {
        // Insert before footer
        const footer = this.element.querySelector(".interior-footer")
        if (footer) {
          footer.insertAdjacentHTML("beforebegin", html)
        }
      }

      this.middleArea = this.element.querySelector("#llm-middle-area")
      this.mode = mode || this.middleArea?.dataset?.mode || "suggestions"
      this.flowActive = this.mode === "flow"
      this.panelActive = this.mode === "panel"
      this.setupLlmSuggestions()

      // Keep hidden - will be revealed after text animation via revealMiddleArea
      if (this.middleArea) {
        this.middleArea.setAttribute("aria-hidden", "true")
      }
      if (this.llmSuggestionsGrid) {
        this.llmSuggestionsGrid.dataset.brushState = "pending"
      }
    }

    // Sync input field placeholder when flow is active
    this.syncFlowInput()
  }

  appendUserBubble(text) {
    if (!this.textContainer) return
    const bubble = document.createElement("div")
    bubble.className = "llm-user-bubble"
    bubble.textContent = text
    bubble.title = "Editar e reenviar"
    this.textContainer.appendChild(bubble)
    this.scrollToBottom()
  }

  showPendingLine() {
    if (!this.textContainer || this.textContainer.querySelector(".llm-pending-line")) return
    const pending = document.createElement("div")
    pending.className = "llm-pending-line"
    pending.setAttribute("aria-label", "O concierge está a escrever")
    pending.setAttribute("role", "status")
    pending.innerHTML = '<span></span><span></span><strong>O concierge está a escrever...</strong>'
    this.textContainer.appendChild(pending)
    this.scrollToBottom()
  }

  removePendingLine() {
    this.textContainer?.querySelectorAll(".llm-pending-line").forEach((node) => node.remove())
  }

  recordIntroSkip() {
    if (!this.hasLlmResponseUrlValue || this.llmRequestInFlight) return
    this.sendAssistantRequest({ event: "intro_skip" })
  }

  injectLlmContent(html) {
    if (!this.textContainer) return
    // Append assistant response instead of replacing (preserves user bubbles and history)
    const wrapper = document.createElement("div")
    wrapper.className = "llm-assistant-message"
    wrapper.innerHTML = html
    this.textContainer.appendChild(wrapper)

    // Re-seleciona os parágrafos recém-injetados (flexível: p, children, ou container)
    this.paragraphs = this.findTextBlocks(wrapper)
  }

  injectSuggestions(html) {
    const existing = this.element.querySelector("#llm-suggestions-area")
    const previousState = this.llmSuggestionsGrid?.dataset?.brushState
    const wasHidden = this.llmSuggestionsGrid?.getAttribute?.("aria-hidden") === "true"

    if (!existing) return
    existing.outerHTML = html

    this.setupLlmSuggestions()

    if (!this.llmSuggestionsGrid) return

	    if (previousState === "revealed") {
	      this.llmSuggestionsGrid.dataset.brushState = "revealed"
	      this.llmSuggestionsGrid.setAttribute("aria-hidden", this.flowActive || this.panelActive ? "true" : wasHidden ? "true" : "false")
	      this.llmSuggestionCards.forEach((card) => {
	        card.classList.add("llm-suggestion-card--brushed")
	        card.disabled = this.llmCardsLocked
	      })
	    }
	  }

  injectFlowUi(html) {
    if (!this.flowUiArea) {
      this.flowUiArea = this.element.querySelector("#flow-ui-area")
    }
    if (!this.flowUiArea) return
    this.flowUiArea.innerHTML = html
    // Sync input field placeholder after flow UI changes
    this.syncFlowInput()
  }

  injectPanel(html) {
    if (!this.panelArea) {
      this.panelArea = this.element.querySelector("#panel-area")
    }
    if (!this.panelArea) return
    this.panelArea.innerHTML = html
    this.panelActive = Boolean(html && String(html).trim().length > 0)
    this.setPanelHidden(!this.panelActive)
  }

  setPanelHidden(isHidden) {
    if (!this.panelArea) return
    this.panelArea.setAttribute("aria-hidden", isHidden ? "true" : "false")
  }

  clearPanel() {
    if (!this.panelArea) return
    this.panelArea.innerHTML = ""
    this.panelActive = false
    this.setPanelHidden(true)
  }

  setCardsHidden(isHidden) {
    if (!this.llmSuggestionsGrid) return
    this.llmSuggestionsGrid.setAttribute("aria-hidden", isHidden ? "true" : "false")
  }

  computeBadgeStorageKey() {
    const token = this.hasGuestTokenValue ? this.guestTokenValue : "unknown"
    return `badgeState:${token}`
  }

  readStoredBadges() {
    if (!this.sessionStore) return {}
    try {
      return JSON.parse(this.sessionStore.getItem(this.badgeStorageKey) || "{}")
    } catch (_) {
      return {}
    }
  }

  storeBadges(map) {
    if (!this.sessionStore) return
    try {
      this.sessionStore.setItem(this.badgeStorageKey, JSON.stringify(map))
    } catch (_) {
      // ignore
    }
  }

  applyBadgesMagicRender({ announce } = {}) {
    const badgesWrap = this.element.querySelector(".llm-badges")
    if (!badgesWrap) return

    const badges = Array.from(badgesWrap.querySelectorAll("[data-badge-key]"))
    if (!badges.length) return

    const previous = this.readStoredBadges()
    const next = {}

    badges.forEach((badge) => {
      const key = badge.dataset.badgeKey || ""
      const state = badge.dataset.badgeState || "locked"
      if (!key) return
      next[key] = state

      const prevState = previous[key]
      const changed = prevState !== state
      const shouldAnimate = changed || typeof prevState === "undefined"

      if (!shouldAnimate) return
      this.magicRevealElement(badge)
    })

    this.storeBadges(next)
    // Note: "Selo desbloqueado" text removed - badge display itself is sufficient
  }

  magicRevealElement(element) {
    if (!element) return
    if (this.prefersReducedMotion()) return
    if (element.dataset.magicAnimated === "1") return
    element.dataset.magicAnimated = "1"

    const direction = element.dataset.magicDirection || "top"
    const distance = 6
    let dx = 0
    let dy = 0
    if (direction === "bottom") dy = distance
    if (direction === "top") dy = -distance
    if (direction === "left") dx = -distance
    if (direction === "right") dx = distance

    // Subtle slide-in without opacity change — badges always legible
    element.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
    element.style.willChange = "transform"

    requestAnimationFrame(() => {
      element.style.transition = "transform 280ms cubic-bezier(0.18, 0.9, 0.22, 1)"
      element.style.transform = "translate3d(0, 0, 0)"
      window.setTimeout(() => {
        element.style.willChange = ""
      }, 320)
    })
  }

  appendAssistantNote(text) {
    if (!this.textContainer || !text) return
    const node = document.createElement("p")
    node.className = "llm-assistant-note"
    node.textContent = text
    this.textContainer.appendChild(node)
    requestAnimationFrame(() => node.classList.add("llm-assistant-note--visible"))
  }

  notificationsThreadActive() {
    const panel = this.element.querySelector('#llm-panel[data-panel-key="notifications"]')
    if (!panel) return false

    const marker = panel.querySelector('[data-notifications-thread-active="true"]')
    return Boolean(marker)
  }

  syncFlowInput() {
    if (!this.llmPromptInput) return

    const defaultPlaceholder = "Escreve a tua pergunta..."
    const flowUi = this.element.querySelector("#flow-ui")
    const inputType = flowUi?.dataset?.inputType || "text"
    const inputPlaceholder = flowUi?.dataset?.inputPlaceholder || ""

    if (this.flowActive && inputPlaceholder) {
      // Flow active with specific placeholder from flow step
      this.llmPromptInput.type = inputType || "text"
      this.llmPromptInput.placeholder = inputPlaceholder
      // Set autocomplete hint for password fields
      if (inputType === "password") {
        this.llmPromptInput.setAttribute("autocomplete", "new-password")
      } else {
        this.llmPromptInput.removeAttribute("autocomplete")
      }
    } else {
      // No flow active or no specific placeholder - always reset to default
      this.llmPromptInput.type = "text"
      this.llmPromptInput.placeholder = defaultPlaceholder
      this.llmPromptInput.removeAttribute("autocomplete")
    }
  }

  revealNewContent() {
    if (!this.paragraphs.length) {
      this.hideGlow()
      return
    }

    // Keep the stage present during responses: dim the middle area instead of
    // emptying it (cards vanishing mid-conversation was measured as M4).
    this.middleArea?.classList.add("llm-middle-area--muted")

    // Reinicia estados de animação
    this.prepareParagraphs()
    this.animationRunId += 1
    const runId = this.animationRunId
    if (this.prefersReducedMotion()) {
      this.paragraphSpans?.forEach((spans) => spans.forEach((span) => span.classList.add("reveal-char--visible")))
      this.hideBright()
      this.middleArea?.classList.remove("llm-middle-area--muted")
      this.revealMiddleArea({ animate: false })
      this.revealFooter({ animate: false })
      this.hideGlow()
      this.markSeen()
      this.magicAnimating = false
      this.hideSkipButton()
      this.scrollToBottom()
      return
    }

    // If the user skipped the intro animation, still animate incoming assistant messages.
    this.magicSkipped = false
    this.magicAnimating = true
    this.showSkipButton()
    this.revealParagraph(0, runId)
  }

  toggleLlmLoading(isLoading) {
    if (this.glow) {
      this.glow.classList.toggle("narration-glow--llm-loading", isLoading)
    }
    if (!this.llmEnvelopeBtn) return
    if (isLoading) this.llmEnvelopeBtn.disabled = true
    this.llmEnvelopeBtn.classList.toggle("is-loading", isLoading)
  }

  llmRequestHeaders() {
    const headers = {
      "Content-Type": "application/json",
    }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      headers["X-CSRF-Token"] = token
    }
    return headers
  }

  lockUi(message = null) {
    this.llmCardsLocked = true
    this.setSuggestionCardsLocked(true)
    if (this.llmEnvelopeBtn) this.llmEnvelopeBtn.disabled = true

    // Usar o sistema unificado de bloqueio de ecrã
    if (window.screenLock) {
      this.currentLockId = window.screenLock.lock(
        `narration-${Date.now()}`,
        message || "A processar..."
      )
    }
  }

  unlockUi() {
    this.middleArea?.classList.remove("llm-middle-area--muted")
    this.llmCardsLocked = false
    this.setSuggestionCardsLocked(false)
    if (this.llmEnvelopeBtn) this.llmEnvelopeBtn.disabled = false

    // Clear loading states from cards and flow buttons
    this.clearActiveElementStates()

    // Desbloquear o ecrã
    if (window.screenLock && this.currentLockId) {
      window.screenLock.unlock(this.currentLockId)
      this.currentLockId = null
    }
  }

  clearActiveElementStates() {
    // Remove loading state from active card
    if (this.activeCard) {
      this.activeCard.classList.remove("llm-suggestion-card--loading")
      this.activeCard = null
    }

    // Remove loading state from active flow button
    if (this.activeFlowButton) {
      this.activeFlowButton.classList.remove("btn--loading", "btn--selected")
      this.activeFlowButton = null
    }

    // Also clear any stray loading states
    this.llmSuggestionCards?.forEach(card => {
      card.classList.remove("llm-suggestion-card--loading")
    })
    this.element.querySelectorAll(".flow-ui__buttons .btn--loading").forEach(btn => {
      btn.classList.remove("btn--loading", "btn--selected")
    })
  }

  clearAndFocusPromptInput() {
    if (!this.llmPromptInput) return
    this.llmPromptInput.value = ""
    this.focusPromptInput()
  }

  focusPromptInput() {
    if (!this.llmPromptInput) return
    requestAnimationFrame(() => {
      this.llmPromptInput?.focus?.()
      try {
        const cursorPosition = this.llmPromptInput.value.length
        this.llmPromptInput?.setSelectionRange?.(cursorPosition, cursorPosition)
      } catch (_) {
        // noop
      }
    })
  }

  setSuggestionCardsLocked(isLocked) {
    if (this.llmSuggestionsGrid) {
      this.llmSuggestionsGrid.setAttribute("aria-busy", isLocked ? "true" : "false")
    }
    if (!this.llmSuggestionCards?.length) return
    this.llmSuggestionCards.forEach((card) => {
      card.disabled = isLocked
      card.classList.toggle("llm-suggestion-card--locked", isLocked)
    })
  }

  // Start BRUSH EFFECT
  setupLlmSuggestions() {
    this.llmSuggestionsGrid = this.element.querySelector('[data-role="llm-suggestions"]')
    if (!this.llmSuggestionsGrid) return
    this.llmSuggestionCards = Array.from(this.llmSuggestionsGrid.querySelectorAll(".llm-suggestion-card"))
    this.setSuggestionCardsLocked(this.llmCardsLocked)
  }

  setupBadgesArea() {
    this.llmBadgesArea = this.element.querySelector("#llm-badges-area")
    this.llmBadgesWrap = this.llmBadgesArea?.querySelector('[data-role="llm-badges"]') ?? null
  }

  prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return true
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  async revealMiddleArea({ animate = true } = {}) {
    if (!this.middleArea) return

    // Reveal the middle area container
    this.middleArea.setAttribute("aria-hidden", "false")

    // Sync input field placeholder when flow is active
    if (this.flowActive) {
      this.syncFlowInput()
      this.focusPromptInput()
    }

    // Check if flow UI has visible buttons (actual interactive content)
    const flowUi = this.middleArea.querySelector("#flow-ui")
    const hasFlowButtons = flowUi?.querySelector(".flow-ui__buttons")

    // If panel is active, just reveal container (no suggestions)
    // If flow has buttons, just reveal container (no suggestions)
    // Otherwise reveal suggestions with brush effect
    if (this.panelActive || (this.flowActive && hasFlowButtons)) {
      return
    }

    // Reveal suggestions with brush effect
    await this.revealLlmSuggestions({ animate })
  }

  async revealLlmSuggestions({ animate = true } = {}) {
    // US-3.4: capture the grid locally — the identity flow can re-render the
    // middle area while the staggered animation below is awaiting, nulling
    // this.llmSuggestionsGrid and crashing the final assignment.
    const grid = this.llmSuggestionsGrid
    if (!grid || !this.llmSuggestionCards?.length) return

    const state = grid.dataset.brushState
    if (state === "revealing" || state === "revealed") return

    grid.setAttribute("aria-hidden", "false")

    const shouldAnimate = animate && !this.prefersReducedMotion()
    if (!shouldAnimate) {
      grid.dataset.brushState = "revealed"
      this.llmSuggestionCards.forEach((card) => {
        card.classList.add("llm-suggestion-card--brushed")
        card.disabled = this.llmCardsLocked
      })
      return
    }

    grid.dataset.brushState = "revealing"

    // M3 fix: no sand-canvas overlay (it rendered as a dirty white veil while
    // waiting for stamp/background downloads). Pure CSS staggered fade+lift
    // using the existing --brushing transition.
    const cards = Array.from(this.llmSuggestionCards)
    await new Promise((resolve) => {
      cards.forEach((card, index) => {
        setTimeout(() => {
          card.disabled = this.llmCardsLocked
          card.classList.add("llm-suggestion-card--brushing")
          requestAnimationFrame(() => card.classList.add("llm-suggestion-card--brushed"))
          if (index === cards.length - 1) setTimeout(resolve, 540)
        }, index * 90)
      })
    })
    grid.dataset.brushState = "revealed"
  }

  revealBadgesArea({ animate = true } = {}) {
    if (!this.llmBadgesArea) return

    const state = this.llmBadgesArea.dataset.brushState
    if (state === "revealed") return

    this.llmBadgesArea.setAttribute("aria-hidden", "false")
    this.llmBadgesArea.dataset.brushState = "revealed"

    if (!this.llmBadgesWrap) return

    const badges = Array.from(this.llmBadgesWrap.querySelectorAll(".llm-badge"))
    if (!badges.length) return

    const shouldAnimate = animate && !this.prefersReducedMotion()
    if (!shouldAnimate) {
      this.applyBadgesMagicRender({ announce: false })
      return
    }

    // Reveal badges one by one with staggered animation
    badges.forEach((badge, index) => {
      setTimeout(() => {
        this.magicRevealElement(badge)
      }, index * 80)
    })

    this.applyBadgesMagicRender({ announce: true })
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async brushRevealSuggestionCard(card) {
    if (!card) return
    if (this.magicSkipped) {
      card.classList.add("llm-suggestion-card--brushed")
      card.disabled = this.llmCardsLocked
      return
    }

    card.disabled = true
    card.classList.add("llm-suggestion-card--brushing")

    const canvas = document.createElement("canvas")
    canvas.className = "llm-suggestion-brush-canvas"
    canvas.setAttribute("aria-hidden", "true")
    card.appendChild(canvas)

    const rect = card.getBoundingClientRect()
    const width = Math.max(1, rect.width)
    const height = Math.max(1, rect.height)
    const dpr = Math.max(1, typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1)

    canvas.width = Math.floor(width * dpr)
    canvas.height = Math.floor(height * dpr)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

	    const ctx = canvas.getContext("2d")
	    if (!ctx) {
	      canvas.remove()
	      card.classList.remove("llm-suggestion-card--brushing")
	      card.classList.add("llm-suggestion-card--brushed")
	      card.disabled = this.llmCardsLocked
	      return
	    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    await this.paintSandCover(ctx, width, height, rect)

    const stamp = await this.getBrushStampCanvas()
    if (stamp) {
      await this.performBrushWipe(ctx, stamp, width, height)
    }

    canvas.classList.add("llm-suggestion-brush-canvas--fade")
    await this.sleep(220)
    canvas.remove()

	    card.classList.remove("llm-suggestion-card--brushing")
	    card.classList.add("llm-suggestion-card--brushed")
	    card.disabled = this.llmCardsLocked
	  }

  async revealFooter({ animate = true } = {}) {
    if (!this.footer) return
    this.footer.classList.add("interior-footer--visible")
  }

  async brushRevealFooterPlate(plate) {
    if (!plate) return
    if (this.magicSkipped) return

    const canvas = document.createElement("canvas")
    canvas.className = "llm-footer-brush-canvas"
    canvas.setAttribute("aria-hidden", "true")
    plate.appendChild(canvas)

    const rect = plate.getBoundingClientRect()
    const width = Math.max(1, rect.width)
    const height = Math.max(1, rect.height)
    const dpr = Math.max(1, typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1)

    canvas.width = Math.floor(width * dpr)
    canvas.height = Math.floor(height * dpr)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`

    if (typeof window !== "undefined" && window.getComputedStyle) {
      const radius = window.getComputedStyle(plate).borderRadius
      if (radius) canvas.style.borderRadius = radius
    }

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      canvas.remove()
      return
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    await this.paintSandCover(ctx, width, height, rect)

    const stamp = await this.getBrushStampCanvas()
    if (stamp) {
      await this.performBrushWipe(ctx, stamp, width, height)
    }

    canvas.classList.add("llm-footer-brush-canvas--fade")
    await this.sleep(220)
    canvas.remove()
  }

  async paintSandCover(ctx, width, height, cardRect) {
    const base = this.readCssColor("--brand-cream", "#e5e0cf")
    ctx.clearRect(0, 0, width, height)

    ctx.globalCompositeOperation = "source-over"
    ctx.globalAlpha = 1
    ctx.fillStyle = base
    ctx.fillRect(0, 0, width, height)

    const bgImage = await this.getPageBackgroundImage()
    this.drawBackgroundSnapshot(ctx, bgImage, cardRect, width, height)

    ctx.globalAlpha = 0.22
    ctx.fillStyle = base
    ctx.fillRect(0, 0, width, height)

    const tileSize = 96
    const tile = document.createElement("canvas")
    tile.width = tileSize
    tile.height = tileSize
    const tctx = tile.getContext("2d")
    if (tctx) {
      const imageData = tctx.createImageData(tileSize, tileSize)
      const data = imageData.data
      for (let i = 0; i < data.length; i += 4) {
        const v = Math.floor(Math.random() * 255)
        data[i] = v
        data[i + 1] = v
        data[i + 2] = v
        data[i + 3] = Math.floor(Math.random() * 36)
      }
      tctx.putImageData(imageData, 0, 0)
    }

    const pattern = ctx.createPattern(tile, "repeat")
    if (pattern) {
      ctx.globalAlpha = 0.16
      ctx.fillStyle = pattern
      ctx.fillRect(0, 0, width, height)
    }

    const vignette = ctx.createRadialGradient(width * 0.5, height * 0.35, 20, width * 0.5, height * 0.5, Math.max(width, height) * 0.85)
    vignette.addColorStop(0, "rgba(255, 255, 255, 0.16)")
    vignette.addColorStop(1, "rgba(90, 60, 44, 0.16)")
    ctx.globalAlpha = 1
    ctx.fillStyle = vignette
    ctx.fillRect(0, 0, width, height)
  }

  async getPageBackgroundImage() {
    if (this.pageBackgroundImage) return this.pageBackgroundImage
    if (this.pageBackgroundPromise) return this.pageBackgroundPromise

    const url = this.pageBackgroundImageUrl()
    this.pageBackgroundPromise = this.tryLoadImage(url).finally(() => {
      this.pageBackgroundPromise = null
    })

    const image = await this.pageBackgroundPromise
    this.pageBackgroundImage = image
    return image
  }

  pageBackgroundImageUrl() {
    if (typeof window === "undefined" || !window.getComputedStyle) return null
    const value = window.getComputedStyle(this.element).backgroundImage || ""

    const matches = [...value.matchAll(/url\((['"]?)(.*?)\1\)/g)]
    if (!matches.length) return null

    const url = matches[0]?.[2]
    return url || null
  }

  drawBackgroundSnapshot(ctx, image, cardRect, width, height) {
    if (!image || !cardRect) return false
    if (typeof window === "undefined" || !window.getComputedStyle) return false

    const heroRect = this.element.getBoundingClientRect()
    const heroWidth = heroRect.width || window.innerWidth || 1
    const heroHeight = heroRect.height || window.innerHeight || 1

    const imgWidth = image.naturalWidth || image.width
    const imgHeight = image.naturalHeight || image.height
    if (!imgWidth || !imgHeight) return false

    const style = window.getComputedStyle(this.element)
    const backgroundSize = (style.backgroundSize || "cover").split(",")[0].trim()

    let drawWidth = imgWidth
    let drawHeight = imgHeight
    if (backgroundSize === "contain") {
      const scale = Math.min(heroWidth / imgWidth, heroHeight / imgHeight)
      drawWidth = imgWidth * scale
      drawHeight = imgHeight * scale
    } else {
      // default: cover (e também qualquer coisa desconhecida)
      const scale = Math.max(heroWidth / imgWidth, heroHeight / imgHeight)
      drawWidth = imgWidth * scale
      drawHeight = imgHeight * scale
    }

    const posX = this.parseBackgroundPosition(style.backgroundPositionX || "50%")
    const posY = this.parseBackgroundPosition(style.backgroundPositionY || "50%")

    const offsetX = this.backgroundOffset(heroWidth, drawWidth, posX)
    const offsetY = this.backgroundOffset(heroHeight, drawHeight, posY)

    const cropX = (cardRect.left - heroRect.left) || 0
    const cropY = (cardRect.top - heroRect.top) || 0

    ctx.save()
    ctx.globalCompositeOperation = "source-over"
    ctx.globalAlpha = 1
    ctx.drawImage(image, offsetX - cropX, offsetY - cropY, drawWidth, drawHeight)
    ctx.restore()

    // Garante que não ficam “buracos” se o cálculo der margem de 1px.
    ctx.save()
    ctx.globalCompositeOperation = "destination-over"
    ctx.globalAlpha = 1
    ctx.fillStyle = this.readCssColor("--brand-cream", "#e5e0cf")
    ctx.fillRect(0, 0, width, height)
    ctx.restore()

    return true
  }

  parseBackgroundPosition(value) {
    const v = String(value || "").trim().toLowerCase()
    if (!v) return { type: "percent", value: 0.5 }
    if (v === "left" || v === "top") return { type: "percent", value: 0 }
    if (v === "center") return { type: "percent", value: 0.5 }
    if (v === "right" || v === "bottom") return { type: "percent", value: 1 }
    if (v.endsWith("%")) {
      const n = Number.parseFloat(v)
      if (Number.isFinite(n)) return { type: "percent", value: Math.max(0, Math.min(1, n / 100)) }
    }
    if (v.endsWith("px")) {
      const n = Number.parseFloat(v)
      if (Number.isFinite(n)) return { type: "px", value: n }
    }
    return { type: "percent", value: 0.5 }
  }

  backgroundOffset(containerSize, imageSize, position) {
    if (!position) return (containerSize - imageSize) * 0.5
    if (position.type === "px") return position.value
    return (containerSize - imageSize) * position.value
  }

  readCssColor(varName, fallback) {
    if (typeof window === "undefined" || !window.getComputedStyle) return fallback
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(varName)?.trim()
    return value || fallback
  }

  async getBrushStampCanvas() {
    if (this.brushStampCanvas) return this.brushStampCanvas
    if (this.brushStampPromise) return this.brushStampPromise

    this.brushStampPromise = this.loadBrushStampCanvas().finally(() => {
      this.brushStampPromise = null
    })

    const canvas = await this.brushStampPromise
    this.brushStampCanvas = canvas
    return canvas
  }

  async loadBrushStampCanvas() {
    const primaryUrl = this.hasBrushStampUrlValue ? this.brushStampUrlValue : null
    const fallbackUrl = this.hasBrushStampFallbackUrlValue ? this.brushStampFallbackUrlValue : null

    const image = (await this.tryLoadImage(primaryUrl)) ?? (await this.tryLoadImage(fallbackUrl))
    if (!image) return null

    const stampCanvas = document.createElement("canvas")
    stampCanvas.width = image.naturalWidth || image.width
    stampCanvas.height = image.naturalHeight || image.height

    const stampCtx = stampCanvas.getContext("2d", { willReadFrequently: true })
    if (!stampCtx) return null

    stampCtx.clearRect(0, 0, stampCanvas.width, stampCanvas.height)
    stampCtx.drawImage(image, 0, 0)

    let imageData
    try {
      imageData = stampCtx.getImageData(0, 0, stampCanvas.width, stampCanvas.height)
    } catch (error) {
      console.warn("Could not read brush image pixels (canvas tainted?)", error)
      return null
    }

    const data = imageData.data
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
      const raw = 255 - luma
      const alpha = Math.max(0, Math.min(255, raw * 1.35 - 18))
      data[i] = 0
      data[i + 1] = 0
      data[i + 2] = 0
      data[i + 3] = alpha
    }

    stampCtx.putImageData(imageData, 0, 0)
    return stampCanvas
  }

  tryLoadImage(url) {
    if (!url) return Promise.resolve(null)
    return new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.decoding = "async"
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = url
    })
  }

  async performBrushWipe(ctx, stamp, width, height) {
    const aspect = stamp.height / stamp.width
    const baseW = width * 1.48
    const baseH = baseW * aspect * 0.26

    const count = 9
    const minY = height * 0.02
    const maxY = height * 0.98

    for (let i = 0; i < count; i += 1) {
      if (this.magicSkipped) return

      const t = count === 1 ? 1 : i / (count - 1)
      const y = minY + (maxY - minY) * t + (Math.random() - 0.5) * height * 0.04
      const angle = (Math.random() - 0.5) * 0.11
      const alpha = Math.max(0.14, Math.min(0.46, 0.18 + t * 0.22 + (Math.random() - 0.5) * 0.06))
      const scale = 0.9 + (Math.random() - 0.5) * 0.12
      const duration = 130 + Math.random() * 120

      await this.animateBrushStroke(ctx, stamp, width, height, baseW, baseH, {
        y,
        angle,
        alpha,
        direction: i % 2 === 0 ? "ltr" : "rtl",
        scale,
        duration,
      })
      await this.sleep(10)
    }

    await this.animateBrushStroke(ctx, stamp, width, height, baseW * 1.12, baseH * 1.12, {
      y: height * 0.5,
      angle: 0,
      alpha: 0.12,
      direction: "ltr",
      scale: 1,
      duration: 180,
    })
  }

  animateBrushStroke(ctx, stamp, width, height, baseW, baseH, stroke) {
    return new Promise((resolve) => {
      if (this.magicSkipped) {
        resolve()
        return
      }

      const duration = Math.max(220, stroke.duration || 520)
      const start = typeof performance !== "undefined" ? performance.now() : Date.now()

      const drawW = baseW * (stroke.scale || 1)
      const drawH = baseH * (stroke.scale || 1)

      const startX = stroke.direction === "rtl" ? width + drawW * 0.48 : -drawW * 0.48
      const endX = stroke.direction === "rtl" ? -drawW * 0.48 : width + drawW * 0.48

      const step = (now) => {
        if (this.magicSkipped) {
          resolve()
          return
        }

        const tRaw = ((now || 0) - start) / duration
        const t = Math.max(0, Math.min(1, tRaw))
        const eased = 1 - Math.pow(1 - t, 3)

        const x = startX + (endX - startX) * eased
        const y = stroke.y + (Math.random() - 0.5) * 2.2

        ctx.save()
        ctx.globalCompositeOperation = "destination-out"
        ctx.globalAlpha = stroke.alpha
        ctx.translate(x, y)
        ctx.rotate(stroke.angle || 0)

        const ox = (Math.random() - 0.5) * 3
        const oy = (Math.random() - 0.5) * 3
        const s = 1 + (Math.random() - 0.5) * 0.035
        ctx.drawImage(stamp, (-drawW * s) / 2 + ox, (-drawH * s) / 2 + oy, drawW * s, drawH * s)

        ctx.restore()

        if (t < 1) {
          requestAnimationFrame(step)
        } else {
          resolve()
        }
      }

      requestAnimationFrame(step)
    })
  }
  // End of BRUSH EFFECT

  forceNextRunForPath(rawUrl) {
    if (!this.sessionStore || !rawUrl) return
    try {
      const url = new URL(rawUrl, window.location.origin)
      const seenKey = this.storagePrefix + url.pathname
      if (this.sessionStore.getItem(seenKey) === "1") return
      this.sessionStore.setItem(this.forcePrefix + url.pathname, "1")
    } catch (error) {
      // ignore invalid URLs
    }
  }

  cleanFromLlmParam() {
    if (typeof window === "undefined" || !window.location) return
    const url = new URL(window.location.href)
    if (!url.searchParams.has("from_llm") && !url.searchParams.has("skip_intro") && !url.searchParams.has("skip_phase")) return
    url.searchParams.delete("from_llm")
    url.searchParams.delete("skip_intro")
    url.searchParams.delete("skip_phase")
    const cleanUrl = url.pathname + url.search + url.hash
    window.history.replaceState(null, "", cleanUrl)
  }

  prepareParagraphs() {
    this.paragraphSpans = this.paragraphs.map((p) => {
      const spans = []

      // Walk through all text nodes while preserving HTML structure (bold, italic, br, etc.)
      const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT, null, false)
      const textNodes = []
      let node
      while ((node = walker.nextNode())) {
        textNodes.push(node)
      }

      textNodes.forEach((textNode) => {
        const text = textNode.textContent
        if (!text) return

        const frag = document.createDocumentFragment()
        for (const ch of text) {
          const span = document.createElement("span")
          span.className = "reveal-char"
          span.textContent = ch
          spans.push(span)
          frag.appendChild(span)
        }
        textNode.parentNode.replaceChild(frag, textNode)
      })

      p.style.whiteSpace = "pre-wrap"
      return spans
    })
  }

  revealAll() {
    // Reveal badges first
    this.revealBadgesArea({ animate: false })
    this.paragraphSpans.forEach((spans) => spans.forEach((span) => span.classList.add("reveal-char--visible")))
    this.hideBright()
    this.markSeen()
    this.hideSkipButton()
    // Reveal middle area (handles both flow/panel and suggestions)
    this.revealMiddleArea({ animate: false }).then(() => this.revealFooter({ animate: false }))
  }

  showGlow() {
    this.glow.style.display = "block"
    requestAnimationFrame(() => {
      this.glow.classList.add("narration-glow--visible", "narration-glow--alive")
    })
    if (this.hero) this.hero.classList.add("interior-hero--dimmed")

    if (this.glowStarsLayer && !this.glowStarsInterval) {
      this.glowStarsInterval = setInterval(() => {
        const isLlmLoading = this.glow?.classList.contains("narration-glow--llm-loading")
        const probability = isLlmLoading ? 0.22 : 0.9
        if (Math.random() < probability) {
          this.spawnGlowCornerStar()
        }
      }, 50)
    }
  }

  hideGlow() {
    this.glow.classList.remove("narration-glow--llm-loading")
    this.glow.classList.remove("narration-glow--visible", "narration-glow--alive")
    this.glow.classList.add("narration-glow--fade")
    if (this.hero) this.hero.classList.remove("interior-hero--dimmed")

    if (this.glowStarsInterval) {
      clearInterval(this.glowStarsInterval)
      this.glowStarsInterval = null
    }
    if (this.glowStarsLayer) {
      this.glowStarsLayer.innerHTML = ""
    }

    setTimeout(() => {
      this.glow.style.display = "none"
      this.glow.classList.remove("narration-glow--fade")
    }, 650)
  }

  celebrateOnce() {
    if (!this.hero) return
    this.hero.classList.add("interior-hero--celebrate")
    window.setTimeout(() => {
      this.hero?.classList?.remove?.("interior-hero--celebrate")
    }, 1200)
  }

  revealParagraph(index, runId) {
    if (this.animationRunId !== runId) return

    if (index >= this.paragraphSpans.length) {
      this.hideBright()
      this.hideGlow()
      setTimeout(async () => {
        if (this.animationRunId !== runId) return
        // Reveal middle area (flow/panel or suggestions) and footer
        this.middleArea?.classList.remove("llm-middle-area--muted")
        await this.revealMiddleArea({ animate: true })
        await this.revealFooter({ animate: true })
        this.markSeen()
        this.magicAnimating = false
        this.hideSkipButton()
        this.scrollToBottom()
      }, 80)
      return
    }

    const spans = this.paragraphSpans[index]

    // If this paragraph has no spans (empty text), skip to next paragraph immediately
    if (!spans || spans.length === 0) {
      this.revealParagraph(index + 1, runId)
      return
    }

    let i = 0

    const step = () => {
      if (this.animationRunId !== runId) return

      if (i < spans.length) {
        this.moveBright(spans[i])
        this.spawnStarFromSpan(spans[i])

        if (i > 0) {
          spans[i - 1].classList.add("reveal-char--visible")
        }

        i += 1
        setTimeout(step, this.typeSpeed)
      } else {
        spans[i - 1].classList.add("reveal-char--visible")
        this.hideBright()
        setTimeout(() => this.revealParagraph(index + 1, runId), this.pauseBetweenParagraphs)
      }
    }

    step()
  }

  moveBright(span) {
    const rect = span.getBoundingClientRect()
    // Center the bright image on the character
    const charCenterX = rect.left + rect.width / 2
    const charCenterY = rect.top + rect.height / 2
    const x = charCenterX - this.brightSize / 2
    const y = charCenterY - this.brightSize / 2

    this.scribeBright.style.left = `${x}px`
    this.scribeBright.style.top = `${y}px`
    this.scribeBright.classList.add("magic-shine")
    this.scribeBright.style.opacity = "1"

    // Auto-scroll to keep the current character in view
    this.autoScrollToSpan(span)
  }

  // Auto-scroll the overlay to keep the revealed text in view
  autoScrollToSpan(span) {
    if (!this.scrollOverlay || !span) return

    const overlayRect = this.scrollOverlay.getBoundingClientRect()
    const spanRect = span.getBoundingClientRect()

    // Calculate how far below the visible area the span is
    // Leave some margin at the bottom (30% of visible height) for comfortable reading
    const visibleBottom = overlayRect.bottom - overlayRect.height * 0.3
    const offsetFromBottom = spanRect.bottom - visibleBottom

    // Only scroll if the span is below the comfortable reading zone
    if (offsetFromBottom > 0) {
      this.scrollOverlay.scrollBy({
        top: offsetFromBottom + 20,
        behavior: "smooth"
      })
    }
  }

  hideBright() {
    this.scribeBright.classList.remove("magic-shine")
    this.scribeBright.style.opacity = "0"
  }

  spawnStarFromSpan(span) {
    if (!this.starsLayer) return
    if (Math.random() > 0.6) return

    const rect = span.getBoundingClientRect()
    const layerRect = this.starsLayer.getBoundingClientRect()

    const star = document.createElement("div")
    star.className = "bright-star " + (Math.random() < 0.5 ? "bright-star--small" : "bright-star--medium")

    const originX = rect.right - layerRect.left
    const originY = rect.bottom - layerRect.top

    star.style.left = `${originX}px`
    star.style.top = `${originY}px`

    const angle = Math.random() * Math.PI - Math.PI / 2
    const distance = 40 + Math.random() * 60
    const tx = Math.cos(angle) * distance
    const ty = Math.sin(angle) * distance - 20

    const rot = Math.random() * 60 - 30 + "deg"

    star.style.setProperty("--star-tx", `${tx}px`)
    star.style.setProperty("--star-ty", `${ty}px`)
    star.style.setProperty("--star-rot", rot)

    this.starsLayer.appendChild(star)
    star.addEventListener("animationend", () => star.remove())
  }

  computeStorageKey() {
    if (typeof window === "undefined" || !window.location) {
      return this.storagePrefix + "default"
    }

    return this.storagePrefix + window.location.pathname
  }

  computeForceKey() {
    if (typeof window === "undefined" || !window.location) {
      return this.forcePrefix + "default"
    }

    return this.forcePrefix + window.location.pathname
  }

  spawnGlowCornerStar() {
    if (!this.glowStarsLayer) return

    const corners = [
      { minX: 8, maxX: 60, minY: 8, maxY: 60 },
      { minX: window.innerWidth - 60, maxX: window.innerWidth - 8, minY: 8, maxY: 60 },
      { minX: 8, maxX: 60, minY: window.innerHeight - 60, maxY: window.innerHeight - 8 },
      { minX: window.innerWidth - 60, maxX: window.innerWidth - 8, minY: window.innerHeight - 60, maxY: window.innerHeight - 8 },
    ]

    const corner = corners[Math.floor(Math.random() * corners.length)]

    const x = corner.minX + Math.random() * (corner.maxX - corner.minX)
    const y = corner.minY + Math.random() * (corner.maxY - corner.minY)

    const star = document.createElement("div")
    star.className = "glow-star " + (Math.random() < 0.5 ? "glow-star--small" : "glow-star--medium")

    star.style.left = `${x}px`
    star.style.top = `${y}px`

    const offsetAngle = (Math.random() * Math.PI) / 3 - Math.PI / 6
    const baseDistance = 10 + Math.random() * 20

    let dirX = 1
    let dirY = 1
    if (corner === corners[0]) {
      dirX = 1
      dirY = 1
    } else if (corner === corners[1]) {
      dirX = -1
      dirY = 1
    } else if (corner === corners[2]) {
      dirX = 1
      dirY = -1
    } else {
      dirX = -1
      dirY = -1
    }

    const tx = dirX * Math.cos(offsetAngle) * baseDistance
    const ty = dirY * Math.sin(offsetAngle) * baseDistance

    star.style.setProperty("--glow-star-tx", `${tx}px`)
    star.style.setProperty("--glow-star-ty", `${ty}px`)

    this.glowStarsLayer.appendChild(star)
    star.addEventListener("animationend", () => star.remove())
  }

  navigationType() {
    if (typeof window === "undefined" || !window.performance) return "navigate"

    const navigation = window.performance.navigation
    if (navigation) {
      if (navigation.type === 1) return "reload"
      if (navigation.type === 2) return "back_forward"
    }

    const entries = window.performance.getEntriesByType("navigation")
    if (entries && entries.length) {
      return entries[0].type
    }

    return "navigate"
  }

  markSeen() {
    if (this.sessionStore) {
      this.sessionStore.setItem(this.storageKey, "1")
    }
    if (!this.hasLlmResponseUrlValue || !this.persistentStore) return
    try {
      this.persistentStore.setItem(this.introStorageKey, "1")
    } catch (_) {
      // localStorage may be unavailable
    }
  }

  persistentIntroSeen() {
    if (!this.persistentStore) return false
    try {
      return this.persistentStore.getItem(this.introStorageKey) === "1"
    } catch (_) {
      return false
    }
  }
}
